import { clamp } from '../../utils/Math';
// eslint-disable-next-line import/no-cycle
import { SliderRange } from './RangeSliderWidget';

/**
 * Logic for dealing with calculating:
 *   - The handle positions of the slider based upon the slider value
 *   - The new slider values from key down events
 */
export class SliderValueLogic {
  private min: number;
  private max: number;
  private roundToIntegers: boolean;

  public static readonly KeysRelevantForManipulatingSlider: {[key: string]: boolean} = Object.freeze({
    'ArrowLeft': true,
    'ArrowRight': true,
    'ArrowUp': true,
    'ArrowDown': true,
    'PageUp': true,
    'PageDown': true,
    'Home': true,
    'End': true
  });

  public constructor(min: number, max: number, roundToIntegers: boolean) {
    this.min = roundToIntegers ? Math.ceil(min) : min;
    this.max = roundToIntegers ? Math.floor(max) : max;
    this.roundToIntegers = roundToIntegers;
  }

  // Range is 0.0 to 1.0, inclusive
  public calculateSliderPositionAsProportion(value: number): number {
    if (this.min > this.max ) {
      return NaN;
    }
    const valuePrime = this.roundToIntegers ? Math.round(value) : value;
    const proportion = (valuePrime - this.min) / (this.max - this.min);
    return clamp(proportion, 0, 1);
  }

  // 0.0 ===> min, 1.0 ===> max
  public calculateNewSliderValueFromProportion(proportion: number): number {
    if (this.min > this.max) {
      return NaN;
    } else if (proportion < 0) {
      return this.min;
    } else if (proportion > 1) {
      return this.max;
    }

    const newValue = this.roundToIntegers ?
      Math.round(proportion * (this.max - this.min)) + this.min :
      proportion * (this.max - this.min) + this.min;

    return clamp(newValue, this.min, this.max);
  }

  public calculateNewSliderValueForKey(key: string, currentValue: number): number | undefined {
    const valuePrime = this.roundToIntegers ? Math.round(currentValue) : currentValue;
    const onePercentOfRange = (this.max - this.min) / 100;
    const tenthOfRange = (this.max - this.min) / 10;
    const arrowKeyChangeAmount = this.roundToIntegers ? 1 : onePercentOfRange;
    const pageKeyChangeAmount = this.roundToIntegers ? Math.max(Math.round(tenthOfRange), 1) : tenthOfRange;

    let newValue = valuePrime;
    switch (key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = valuePrime - arrowKeyChangeAmount;
        break;
      case 'ArrowUp':
      case 'ArrowRight':
        newValue = valuePrime + arrowKeyChangeAmount;
        break;
      case 'PageDown':
        newValue = valuePrime - pageKeyChangeAmount;
        break;
      case 'PageUp':
        newValue = valuePrime + pageKeyChangeAmount;
        break;
      case 'Home':
        newValue = this.min;
        break;
      case 'End':
        newValue = this.max;
        break;
      default:
        return undefined;
    }
    return clamp(newValue, this.min, this.max);
  }

  public calculateNewSliderRangeValueForKey(key: string, currentValue: SliderRange): SliderRange | undefined {
    const currentValueDelta = Math.max(currentValue.to - currentValue.from, 0);
    const arrowKeyChangeAmount = (this.max - this.min) / 100;
    const pageKeyChangeAmount = (this.max - this.min) / 10;

    let newValue = currentValue;
    switch (key) {
      case 'ArrowDown':
      case 'ArrowLeft':
        if (currentValue.from > this.min) {
          const newFromValue = Math.max(currentValue.from - arrowKeyChangeAmount, this.min);
          newValue = { from: newFromValue, to: newFromValue + currentValueDelta };
        }
        break;
      case 'ArrowUp':
      case 'ArrowRight':
        if (currentValue.to < this.max) {
          const newToValue = Math.min(currentValue.to + arrowKeyChangeAmount, this.max);
          newValue = { from: newToValue - currentValueDelta, to: newToValue };
        }
        break;
      case 'PageDown':
        if (currentValue.from > this.min) {
          const newFromValue = Math.max(currentValue.from - pageKeyChangeAmount, this.min);
          newValue = { from: newFromValue, to: newFromValue + currentValueDelta };
        }
        break;
      case 'PageUp':
        if (currentValue.to < this.max) {
          const newToValue = Math.min(currentValue.to + pageKeyChangeAmount, this.max);
          newValue = { from: newToValue - currentValueDelta, to: newToValue };
        }
        break;
      case 'Home':
        newValue = { from: this.min, to: this.min + currentValueDelta };
        break;
      case 'End':
        newValue = { from: this.max - currentValueDelta, to: this.max };
        break;
      default:
        return undefined;
    }
    return { from: clamp(newValue.from, this.min, this.max), to: clamp(newValue.to, this.min, this.max) };
  }
}
